import * as React from 'react';

import { useRouter } from 'next/router';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField as MuiTextField,
} from '@mui/material';

import { useUser } from '@itp/talent';
import { ButtonPrimary, ButtonSecondary } from '@itp/talent/components/core/button';
import { useFetchMutation } from '@itp/talent/hooks/useFetch';

interface ClaimResult {
  claimRequestId?: string;
  companyClaimed: boolean;
  companyId?: string;
  jobsLinked: number;
  profileId?: string;
  profileLinked: boolean;
  success: boolean;
}

interface UnclaimedCompany {
  id: string;
  jobCount: number;
  name: string;
  website: null | string;
}

interface UnclaimedResponse {
  companies: UnclaimedCompany[];
  success: boolean;
}

interface WebsiteClaimResult {
  claimRequestId: string;
  companyId: string;
  role: string;
  success: boolean;
}

export const DropClaimHandler: React.FC = () => {
  const router = useRouter();
  const { refetch, user } = useUser();
  const claimedRef = React.useRef(false);

  const [showDialog, setShowDialog] = React.useState(false);
  const [unclaimedCompanies, setUnclaimedCompanies] = React.useState<UnclaimedCompany[]>([]);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [websiteUrl, setWebsiteUrl] = React.useState('');
  const [claimError, setClaimError] = React.useState('');

  const { mutateAsync: claimDrop } = useFetchMutation<ClaimResult, Record<string, never>>(
    { method: 'POST', url: '/drop/claim' },
    { messageFail: null, messageSuccess: null },
  );

  const { mutateAsync: fetchUnclaimed } = useFetchMutation<
    UnclaimedResponse,
    Record<string, never>
  >(
    { method: 'GET', url: '/drop/claim/unclaimed-companies' },
    { messageFail: null, messageSuccess: null },
  );

  const { isPending: isClaimingWebsite, mutateAsync: claimCompanyWebsite } = useFetchMutation<
    WebsiteClaimResult,
    { companyId: string; website: string }
  >(
    { method: 'POST', url: '/drop/claim/company-website' },
    { messageFail: null, messageSuccess: null },
  );

  const selectedCompany = unclaimedCompanies[selectedIndex];

  const openDialog = React.useCallback((companies: UnclaimedCompany[]) => {
    setUnclaimedCompanies(companies);
    setSelectedIndex(0);
    setWebsiteUrl(companies[0]?.website || '');
    setClaimError('');
    setShowDialog(true);
  }, []);

  const handleClaimSubmit = React.useCallback(async () => {
    if (!selectedCompany || !websiteUrl.trim()) {
      setClaimError('Please enter a website URL');
      return;
    }

    setClaimError('');

    try {
      await claimCompanyWebsite({
        companyId: selectedCompany.id,
        website: websiteUrl.trim(),
      });

      enqueueSnackbar(`${selectedCompany.name} claimed successfully!`, {
        autoHideDuration: 5000,
        variant: 'success',
      });

      const remaining = unclaimedCompanies.filter((_, i) => i !== selectedIndex);

      if (remaining.length > 0) {
        setUnclaimedCompanies(remaining);
        setSelectedIndex(0);
        setWebsiteUrl(remaining[0]?.website || '');
        setClaimError('');
      } else {
        setShowDialog(false);
      }

      await refetch?.();
    } catch (err: any) {
      setClaimError(err?.response?.data?.error || err?.message || 'Failed to claim company');
    }
  }, [
    selectedCompany,
    websiteUrl,
    claimCompanyWebsite,
    unclaimedCompanies,
    selectedIndex,
    refetch,
  ]);

  React.useEffect(() => {
    if (claimedRef.current) return;
    if (!router.isReady) return;
    if (router.query.claim_drop !== '1') return;
    if (!user?.id || user.loading) return;

    claimedRef.current = true;

    (async () => {
      try {
        const result = await claimDrop({});

        if (result?.profileLinked || (result?.jobsLinked ?? 0) > 0 || result?.companyClaimed) {
          const parts: string[] = [];
          if (result.profileLinked) parts.push('CV profile linked');
          if (result.jobsLinked > 0) parts.push(`${result.jobsLinked} job(s) linked`);
          if (result.companyClaimed) parts.push('company claimed');
          enqueueSnackbar(`${parts.join(', ')} to your account`, {
            autoHideDuration: 5000,
            variant: 'success',
          });
          await refetch?.();
        }

        if ((result?.jobsLinked ?? 0) > 0 && !result?.companyClaimed) {
          try {
            const unclaimed = await fetchUnclaimed({});
            if (unclaimed?.companies?.length > 0) {
              const companies = unclaimed.companies;
              enqueueSnackbar(
                `You have ${companies.length} company${companies.length > 1 ? 'ies' : ''} to claim.`,
                {
                  action: (key) => (
                    <ButtonPrimary
                      onClick={() => {
                        closeSnackbar(key);
                        openDialog(companies);
                      }}
                      size="small"
                    >
                      Claim
                    </ButtonPrimary>
                  ),
                  autoHideDuration: 15000,
                  variant: 'info',
                },
              );
            }
          } catch {}
        }
      } catch (err) {
        console.error('Drop claim failed:', err);
      } finally {
        const { claim_drop, ...rest } = router.query;
        router.replace({ pathname: router.pathname, query: rest }, undefined, { shallow: true });
      }
    })();
  }, [
    router.isReady,
    router.query.claim_drop,
    user?.id,
    user?.loading,
    claimDrop,
    fetchUnclaimed,
    openDialog,
    refetch,
    router,
  ]);

  if (!showDialog || !selectedCompany) return null;

  return (
    <Dialog fullWidth maxWidth="sm" onClose={() => setShowDialog(false)} open={showDialog}>
      <DialogTitle>Claim {selectedCompany.name}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 3 }}>
          {selectedCompany.jobCount > 0
            ? `You have ${selectedCompany.jobCount} job${selectedCompany.jobCount > 1 ? 's' : ''} linked to this company. `
            : ''}
          Enter your company website to verify ownership and manage your company on TalentProfile.
        </DialogContentText>

        <MuiTextField
          onChange={(e) => {
            setWebsiteUrl(e.target.value);
            setClaimError('');
          }}
          autoFocus
          disabled={isClaimingWebsite}
          error={!!claimError}
          fullWidth
          helperText={claimError || 'Your email domain must match the website domain'}
          label="Company Website"
          placeholder="https://example.com"
          value={websiteUrl}
        />

        {unclaimedCompanies.length > 1 && (
          <DialogContentText sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 2 }}>
            {selectedIndex + 1} of {unclaimedCompanies.length} companies
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions sx={{ pb: 2, px: 3 }}>
        <ButtonSecondary onClick={() => setShowDialog(false)} size="medium">
          Skip
        </ButtonSecondary>
        <ButtonPrimary
          disabled={!websiteUrl.trim() || isClaimingWebsite}
          loading={isClaimingWebsite}
          onClick={handleClaimSubmit}
          size="medium"
        >
          Verify & Claim
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  );
};
